/**
 * DerelictOverlay.js
 *
 * Phase 3 (June 2026) — Ship's-log console for a boarded derelict.
 *
 * Opened when the player walks up to the glowing log terminal inside a wreck
 * and presses E. Shows the procedurally-AI-generated final log, the wreck's
 * fate, a manifest of looted/remaining containers, and a "Salvage Hull"
 * action (breaks the hull down into raw materials + credits).
 *
 * Physical looting of individual containers happens in the 3D (walk up + E);
 * this console is purely the log + salvage + status readout.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, Skull, Wrench, FileText, ShieldAlert, PackageCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function DerelictOverlay({ open, api, derelictId, updateCredits, onSalvaged, onClose }) {
  const [detail, setDetail] = useState(null);
  const [salvaging, setSalvaging] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!derelictId) return;
    try {
      const { data } = await api.get('/api/derelicts/detail', { params: { id: derelictId } });
      setDetail(data);
    } catch (_e) {
      toast.error('Unable to read the ship\'s log');
    }
  }, [api, derelictId]);

  useEffect(() => {
    if (open && derelictId) fetchDetail();
  }, [open, derelictId, fetchDetail]);

  const doSalvage = useCallback(async () => {
    if (!derelictId || salvaging) return;
    setSalvaging(true);
    try {
      const { data } = await api.post('/api/derelicts/salvage', { id: derelictId });
      if (data?.credits != null) updateCredits?.(data.credits);
      const r = data?.reward || {};
      const parts = [];
      if (r.credits) parts.push(`${r.credits} cr`);
      (r.ore || []).forEach((o) => parts.push(`${o.qty}u ${o.name}`));
      toast.success(`Hull salvaged · ${parts.join(' · ')}`);
      onSalvaged?.();
      fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Salvage failed');
    } finally {
      setSalvaging(false);
    }
  }, [api, derelictId, salvaging, updateCredits, onSalvaged, fetchDetail]);

  if (!open) return null;

  const ready = detail && detail.id === derelictId;
  const guardedBlocked = ready && detail.guarded && !detail.cleared;
  const lootedCount = (ready ? detail.containers || [] : []).filter((c) => c.looted).length;
  const totalContainers = (ready ? detail.containers || [] : []).length;
  const sv = (ready && detail.salvage_preview) || {};

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="derelict-overlay"
    >
      <div className="w-full max-w-lg rounded-2xl border border-cyan-500/30 bg-[#070b12] shadow-[0_0_60px_rgba(20,160,200,0.25)] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-950/40 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-cyan-400/70">Ship&apos;s Log · Recovered</div>
              <div className="text-lg font-semibold text-cyan-50" data-testid="derelict-name">
                {detail?.name || 'Derelict'}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="derelict-close-btn"
            className="text-slate-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {!ready && (
            <div className="flex items-center justify-center py-10 text-cyan-300/70" data-testid="derelict-loading">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {ready && (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-cyan-400/30 text-cyan-200 capitalize">{detail.klass}</Badge>
                <Badge variant="outline" className="border-slate-500/30 text-slate-300 capitalize">{detail.size} hull</Badge>
                {detail.guarded && !detail.cleared && (
                  <Badge className="bg-red-500/20 text-red-300 border border-red-400/40">
                    <ShieldAlert className="w-3 h-3 mr-1" /> Hostiles active
                  </Badge>
                )}
                {detail.guarded && detail.cleared && (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">Secured</Badge>
                )}
              </div>

              {/* AI Log */}
              <div className="rounded-xl border border-cyan-500/15 bg-cyan-950/10 p-4">
                <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-cyan-400/60 mb-2">
                  <Skull className="w-3.5 h-3.5" /> Final Transmission
                </div>
                <p className="text-sm leading-relaxed text-cyan-100/90 italic whitespace-pre-line" data-testid="derelict-log-text">
                  {detail.log}
                </p>
                {detail.cause && (
                  <div className="mt-3 text-xs text-slate-400">
                    Cause of loss: <span className="text-slate-300">{detail.cause}</span>
                  </div>
                )}
              </div>

              {/* Container manifest */}
              <div className="rounded-xl border border-slate-600/20 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-slate-400">
                    <PackageCheck className="w-3.5 h-3.5" /> Cargo Manifest
                  </div>
                  <span className="text-xs text-slate-400" data-testid="derelict-loot-progress">
                    {lootedCount}/{totalContainers} salvaged
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(detail.containers || []).map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs border ${
                        c.looted
                          ? 'border-slate-700/40 bg-slate-800/20 text-slate-500 line-through'
                          : 'border-amber-400/25 bg-amber-500/5 text-amber-100'
                      }`}
                      data-testid={`derelict-container-${c.id}`}
                    >
                      {c.label}
                    </div>
                  ))}
                </div>
                {!guardedBlocked && totalContainers > lootedCount && (
                  <p className="mt-3 text-[11px] text-slate-500">
                    Walk to the crates aboard the wreck and press <span className="text-cyan-300">E</span> to loot them.
                  </p>
                )}
              </div>

              {/* Salvage */}
              {guardedBlocked ? (
                <div className="rounded-xl border border-red-500/25 bg-red-950/15 p-4 text-sm text-red-200" data-testid="derelict-guard-warning">
                  <ShieldAlert className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  Guard drones are still active. Destroy them before you can salvage or loot this wreck.
                </div>
              ) : detail.salvaged ? (
                <div className="rounded-xl border border-slate-600/20 bg-white/[0.02] p-4 text-sm text-slate-400" data-testid="derelict-salvaged-note">
                  This hull has already been stripped for materials.
                </div>
              ) : (
                <div className="rounded-xl border border-orange-400/20 bg-orange-500/5 p-4">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-orange-300/70 mb-1.5">
                    <Wrench className="w-3.5 h-3.5" /> Salvage Hull
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    Break the wreck down into raw materials:&nbsp;
                    <span className="text-orange-200">
                      {(sv.ore || []).map((o) => `${o.qty}u ${o.name}`).join(' · ')}
                      {sv.credits ? ` · +${sv.credits} cr` : ''}
                    </span>
                  </p>
                  <Button
                    onClick={doSalvage}
                    disabled={salvaging}
                    data-testid="derelict-salvage-btn"
                    className="w-full bg-orange-500/90 hover:bg-orange-400 text-slate-900 font-semibold"
                  >
                    {salvaging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                    Salvage Hull
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DerelictOverlay;
